import { createElement } from "react";
import type { CSSProperties } from "react";
import {
  useCanAccess,
  useCreatePath,
  useGetResourceLabel,
  useHasDashboard,
  usePermissions,
  useResourceDefinitions,
  useStore,
  useTranslate,
  LinkBase,
  useMatch,
} from "ra-core";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Skeleton } from "../ui/skeleton";
import { ChevronRight, House, List } from "lucide-react";
import hatiHealthIcon from "./hati-health-icon.png";
import type { UserScope } from "../../lib/userScopes";

const MARKETING_CATEGORY = "Marketing";
// Typed against the shared `USER_SCOPES` vocabulary (HATCH-668 review) so
// this can't silently drift from the users edit form's own scope enum.
const MARKETING_SCOPE: UserScope = "marketing";

/**
 * Navigation sidebar displaying menu items, allowing users to navigate between different sections of the application.
 *
 * The sidebar can collapse to an icon-only view and renders as a collapsible drawer on mobile devices.
 * It automatically includes links to the dashboard (if defined) and all list views defined in Resource components.
 *
 * Included in the default Layout component
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/appsidebar AppSidebar documentation}
 * @see {@link https://ui.shadcn.com/docs/components/sidebar shadcn/ui Sidebar component}
 * @see layout.tsx
 *
 * `style` is an optional passthrough onto the underlying `Sidebar`'s own
 * `fixed inset-y-0 ... h-svh` container, unused by this default `Layout` —
 * it exists for an app with its own extra fixed/sticky chrome above the
 * sidebar (e.g. specialists-portal's own `Layout`, HATCH-814: a persistent
 * top status bar) that needs the sidebar's fixed box to start below it and
 * shrink to match, rather than the sidebar overlapping it. Inline `style`
 * rather than `className`, since it must unconditionally win over the
 * component's own `top`/`height` utility classes regardless of Tailwind's
 * internal utility-group ordering.
 */
export function AppSidebar({ style }: { style?: CSSProperties } = {}) {
  const hasDashboard = useHasDashboard();
  const resources = useResourceDefinitions();
  const { permissions } = usePermissions<string[]>();
  // A user scoped to marketing-only access (HATCH-668) sees just the
  // Marketing category — no dashboard, no other groups.
  const marketingOnly = Array.isArray(permissions) && permissions.includes(MARKETING_SCOPE);
  const { openMobile, setOpenMobile } = useSidebar();
  const handleClick = () => {
    if (openMobile) {
      setOpenMobile(false);
    }
  };

  const listResourceNames = Object.keys(resources).filter(
    (name) => resources[name].hasList,
  );
  // Navigation is designed, not inherited from Retool's flat app list
  // (migration guide §3.5): a resource opts into a named group via
  // `options={{ category: '...' }}` on its `<Resource>` registration in
  // App.tsx; one that doesn't set it stays in the plain top-level list
  // (every resource before HATCH-35, and any future one that doesn't need
  // grouping yet).
  const uncategorizedNames: string[] = [];
  const categoryOrder: string[] = [];
  const namesByCategory = new Map<string, string[]>();
  for (const name of listResourceNames) {
    const category = resources[name].options?.category as string | undefined;
    if (!category) {
      uncategorizedNames.push(name);
      continue;
    }
    if (!namesByCategory.has(category)) {
      categoryOrder.push(category);
      namesByCategory.set(category, []);
    }
    namesByCategory.get(category)!.push(name);
  }

  return (
    <Sidebar variant="floating" collapsible="icon" style={style}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <LinkBase to="/">
                <img
                  src={hatiHealthIcon}
                  alt="Hati Health"
                  className="!size-5 rounded-sm"
                />
                <span className="text-base font-semibold">Hati Health</span>
              </LinkBase>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {!marketingOnly && (hasDashboard || uncategorizedNames.length > 0) ? (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {hasDashboard ? (
                  <DashboardMenuItem onClick={handleClick} />
                ) : null}
                {uncategorizedNames.map((name) => (
                  <ResourceMenuItem key={name} name={name} onClick={handleClick} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
        {categoryOrder
          .filter((category) => !marketingOnly || category === MARKETING_CATEGORY)
          .map((category) => (
            <CategoryGroup
              key={category}
              category={category}
              names={namesByCategory.get(category)!}
              onClick={handleClick}
            />
          ))}
      </SidebarContent>
    </Sidebar>
  );
}

/**
 * Collapsible sidebar group for a nav category (§3.5), collapsed by default.
 * Its open/closed state is persisted per category in the store (localStorage
 * via `localStorageStore`, HATCH-111), so it's remembered across reloads.
 *
 * @example
 * <CategoryGroup category="Storefront" names={["products", "brands"]} onClick={handleClick} />
 */
export const CategoryGroup = ({
  category,
  names,
  onClick,
}: {
  category: string;
  names: string[];
  onClick?: () => void;
}) => {
  const [open, setOpen] = useStore(`sidebar.category.${category}`, false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarGroup>
        <SidebarGroupLabel asChild>
          <CollapsibleTrigger className="cursor-pointer">
            {category}
            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              {names.map((name) => (
                <ResourceMenuItem key={name} name={name} onClick={onClick} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
};

/**
 * Menu item for the dashboard link in the sidebar.
 *
 * This component renders a sidebar menu item that links to the dashboard page.
 * It displays as active when the user is on the dashboard route.
 *
 * @example
 * <DashboardMenuItem onClick={handleClick} />
 */
export const DashboardMenuItem = ({ onClick }: { onClick?: () => void }) => {
  const translate = useTranslate();
  const label = translate("ra.page.dashboard", {
    _: "Dashboard",
  });
  const match = useMatch({ path: "/", end: true });
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={!!match}>
        <LinkBase to="/" onClick={onClick}>
          <House />
          {label}
        </LinkBase>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

/**
 * Menu item for a resource link in the sidebar.
 *
 * This component renders a sidebar menu item that links to a resource's list view.
 * It checks permissions using canAccess and displays as active when the user is viewing that resource.
 * The component icon and label are derived from the resource definition.
 *
 * @example
 * <ResourceMenuItem key={name} name="posts" onClick={handleClick} />
 */
export const ResourceMenuItem = ({
  name,
  onClick,
}: {
  name: string;
  onClick?: () => void;
}) => {
  const { canAccess, isPending } = useCanAccess({
    resource: name,
    action: "list",
  });
  const resources = useResourceDefinitions();
  const getResourceLabel = useGetResourceLabel();
  const createPath = useCreatePath();
  const to = createPath({
    resource: name,
    type: "list",
  });
  const match = useMatch({ path: to, end: false });

  if (isPending) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (!resources || !resources[name] || !canAccess) return null;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={!!match}>
        <LinkBase to={to} state={{ _scrollToTop: true }} onClick={onClick}>
          {resources[name].icon ? (
            createElement(resources[name].icon)
          ) : (
            <List />
          )}
          {getResourceLabel(name, 2)}
        </LinkBase>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
